import React, { createContext, FC, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@modules/me';
import { SettingsContext, getCountry, getCountryId } from '@modules/settings';

export type CountryContextValue = [{ selected: string | null; available: string[] }, { onSelectedCountryChange: (value: string) => void }];

export const CountryContext = createContext<CountryContextValue>([{ selected: null, available: [] }, { onSelectedCountryChange: () => {} }]);

export const CountryProvider: FC<PropsWithChildren> = ({ children }) => {
  const { can } = useAuth();
  const settings = useContext(SettingsContext);

  const available = useMemo(() => {
    const countries: string[] = [];

    if (settings.countries) {
      settings.countries.forEach((country) => {
        if (can(`warehouse_${country.abbr}`)) {
          countries.push(country.abbr);
        }
      });
    }

    return countries;
  }, [can, settings.countries]);

  const [selected, setSelected] = useState<string | null>(() => {
    const country = localStorage.getItem('warehouse.country');
    return country || null;
  });

  useEffect(() => {
    if (selected && settings.countries) {
      const countryId = getCountryId(settings.countries, selected);
      if (countryId !== null) {
        localStorage.setItem('warehouse.country_id', countryId.toString());
      }

      const country = countryId !== null ? getCountry(settings.countries, countryId) : null;
      if (country) {
        localStorage.setItem('warehouse.measure', country.unit);
      }
    }
  }, [selected, settings]);

  const onSelectedCountryChange = useCallback((country: string) => {
    setSelected(country || null);
    localStorage.setItem('warehouse.country', country || '');
  }, []);

  const value = useMemo<CountryContextValue>(() => [{ selected, available }, { onSelectedCountryChange }], [available, onSelectedCountryChange, selected]);

  return <CountryContext.Provider value={value}>{children}</CountryContext.Provider>;
};
